import {
  LeadListScope,
  LeadSearchMatchReason,
  LeadsApi,
  type Lead,
} from '@up2stay/up2promo-api-client';
import { formatLeadDisplayName } from '@up2stay/up2promo-commons';
import { cn, Input } from '@up2stay/up2promo-ui';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  ChevronRight,
  History,
  List,
  Lock,
  Search,
  StickyNote,
  User,
  X,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { highlightQuery } from '@/core/components/leads/leads-search-highlight';
import { leadInitials } from '@/core/components/leads/leads-list-display';
import { listToolbarSearchInputClass } from '@/core/components/layout/list-toolbar-styles';
import { useIsMobile } from '@/core/hooks/use-mobile';

/** Our own clear button replaces the browser's (no double ×, DEV-567). */
const HIDE_NATIVE_SEARCH_CLEAR =
  '[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-cancel-button]:hidden';

const RECENT_STORAGE_KEY = 'up2promo.leads.recentSearches';
const MAX_RECENT = 6;

function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

function readRecentSearches(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item): item is string => typeof item === 'string')
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function writeRecentSearches(items: string[]): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    RECENT_STORAGE_KEY,
    JSON.stringify(items.slice(0, MAX_RECENT)),
  );
}

function matchReasonLabel(
  reason: LeadSearchMatchReason | undefined,
  labels: Record<string, string>,
): string {
  if (!reason) return labels.name ?? 'Name';
  return labels[reason] ?? reason;
}

function MatchReasonIcon({
  reason,
}: {
  reason: LeadSearchMatchReason | undefined;
}) {
  const className = 'size-[11px] shrink-0';
  switch (reason) {
    case LeadSearchMatchReason.NOTE:
    case LeadSearchMatchReason.MESSAGE:
      return <StickyNote className={className} aria-hidden />;
    case LeadSearchMatchReason.COMPANY:
    case LeadSearchMatchReason.JOB_TITLE:
      return <Building2 className={className} aria-hidden />;
    default:
      return <User className={className} aria-hidden />;
  }
}

function reasonMeta(
  lead: Lead,
  query: string,
  labels: LeadsSearchLabels,
): { icon: ReactNode; text: ReactNode } {
  const reasons = lead.matchReasons ?? [];
  const primary = reasons[0];
  const isNote =
    primary === LeadSearchMatchReason.NOTE ||
    primary === LeadSearchMatchReason.MESSAGE;
  const excerpt = lead.matchExcerpt?.trim();

  if (isNote && excerpt) {
    const [before = '', after = ''] = labels.noteExcerpt.split('{excerpt}');
    return {
      icon: <MatchReasonIcon reason={primary} />,
      text: (
        <>
          {before}
          {highlightQuery(excerpt, query)}
          {after}
        </>
      ),
    };
  }

  const joined = reasons
    .slice(0, 3)
    .map((r) => matchReasonLabel(r, labels.matchReasons))
    .join(', ');

  return {
    icon: <MatchReasonIcon reason={primary} />,
    text: joined || matchReasonLabel(primary, labels.matchReasons),
  };
}

type CompanySuggestion = {
  name: string;
  count: number;
};

function collectCompanySuggestions(leads: Lead[]): CompanySuggestion[] {
  const map = new Map<string, CompanySuggestion>();
  for (const lead of leads) {
    const company = lead.companyName?.trim();
    if (!company) continue;
    const reasons = lead.matchReasons ?? [];
    if (!reasons.includes(LeadSearchMatchReason.COMPANY)) continue;
    const key = company.toLowerCase();
    const existing = map.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      map.set(key, { name: company, count: 1 });
    }
  }
  return [...map.values()].slice(0, 4);
}

export type LeadsSearchLabels = {
  placeholder: string;
  clearAriaLabel: string;
  cancelLabel: string;
  recentHeading: string;
  quickFiltersHeading: string;
  leadsHeading: string;
  companiesHeading: string;
  companyPrefix: string;
  companyFilterHint: string;
  companyFilterHintPlural: string;
  noteExcerpt: string;
  assignedToMe: string;
  statusNew: string;
  showAllInList: string;
  chooseHint: string;
  openHint: string;
  emptyTitle: string;
  didYouMean: string;
  resetFilters: string;
  searchInWorkspace: string;
  othersPrivateHint: string;
  matchReasons: Record<string, string>;
  scopeMine: string;
  scopeWorkspace: string;
  scopeOthersPrivate: string;
};

type LeadsSearchFieldProps = {
  workspaceId: string | undefined;
  scope: LeadListScope;
  value: string;
  onChange: (next: string) => void;
  onCommit: (query: string) => void;
  onQuickFilterAssignedToMe: () => void;
  onQuickFilterStatusNew: () => void;
  onQuickFilterEvent?: (eventId: string) => void;
  eventQuickFilters?: Array<{ id: string; name: string }>;
  onOpenLead: (leadId: string) => void;
  onSwitchToWorkspaceScope?: () => void;
  onScopeChange?: (scope: LeadListScope) => void;
  onResetFilters?: () => void;
  scopeCounts?: {
    mine: number;
    workspace: number;
    othersPrivate: number;
  };
  canSeeOthersPrivate?: boolean;
  statusLabels: Record<string, string>;
  labels: LeadsSearchLabels;
  className?: string;
  /** Override search input chrome (list toolbar uses compact surface-2). */
  inputClassName?: string;
  /** Desktop idle “/” shortcut hint. Default true. */
  showSlashHint?: boolean;
  /** Increment to open the mobile overlay / focus desktop (header search icon). */
  openRequestId?: number;
  /** Hide the idle input chrome (mobile header owns the search button). */
  hideIdleTrigger?: boolean;
};

export function LeadsSearchField({
  workspaceId,
  scope,
  value,
  onChange,
  onCommit,
  onQuickFilterAssignedToMe,
  onQuickFilterStatusNew,
  onQuickFilterEvent,
  eventQuickFilters = [],
  onOpenLead,
  onSwitchToWorkspaceScope,
  onScopeChange,
  onResetFilters,
  scopeCounts,
  canSeeOthersPrivate = false,
  statusLabels,
  labels,
  className,
  inputClassName,
  showSlashHint = true,
  openRequestId = 0,
  hideIdleTrigger = false,
}: LeadsSearchFieldProps) {
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const desktopPanelRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [desktopPanelStyle, setDesktopPanelStyle] = useState<CSSProperties>({
    position: 'fixed',
    top: 0,
    left: 0,
    width: 0,
    zIndex: 80,
  });
  const [handledOpenRequestId, setHandledOpenRequestId] = useState(0);
  if (openRequestId > 0 && openRequestId !== handledOpenRequestId) {
    setHandledOpenRequestId(openRequestId);
    setOpen(true);
  }
  const [draft, setDraft] = useState(value);
  const [syncedValue, setSyncedValue] = useState(value);
  const [recent, setRecent] = useState<string[]>(() => readRecentSearches());
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [highlightResetKey, setHighlightResetKey] = useState(`${value}|0|idle`);
  const debouncedDraft = useDebouncedValue(draft.trim(), 220);
  const resolvedStatusLabels = statusLabels;

  if (value !== syncedValue) {
    setSyncedValue(value);
    setDraft(value);
  }

  const suggestEnabled = open && !!workspaceId && debouncedDraft.length >= 1;

  const { data: suggestPayload, isFetched: suggestFetched } = useQuery({
    queryKey: ['leads-suggest', workspaceId, scope, debouncedDraft] as const,
    queryFn: async () => {
      const res = await LeadsApi.find({
        path: { workspaceId: workspaceId! },
        query: {
          q: debouncedDraft,
          scope,
          suggest: true,
          limit: 8,
        },
      });
      return {
        items: res.data?.items ?? [],
        suggestions: res.data?.suggestions ?? [],
      };
    },
    enabled: suggestEnabled,
    staleTime: 15_000,
  });

  const suggestions = suggestPayload?.items ?? [];
  const fuzzySuggestions = suggestPayload?.suggestions ?? [];
  const companySuggestions = collectCompanySuggestions(suggestions);

  const rememberRecent = useCallback((query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setRecent((prev) => {
      const next = [
        trimmed,
        ...prev.filter((item) => item.toLowerCase() !== trimmed.toLowerCase()),
      ].slice(0, MAX_RECENT);
      writeRecentSearches(next);
      return next;
    });
  }, []);

  const removeRecent = useCallback((query: string) => {
    setRecent((prev) => {
      const next = prev.filter(
        (item) => item.toLowerCase() !== query.toLowerCase(),
      );
      writeRecentSearches(next);
      return next;
    });
  }, []);

  const commit = useCallback(
    (query: string) => {
      const trimmed = query.trim();
      onChange(trimmed);
      onCommit(trimmed);
      if (trimmed) rememberRecent(trimmed);
      setOpen(false);
    },
    [onChange, onCommit, rememberRecent],
  );

  useEffect(() => {
    if (!open) return;
    if (isMobile) {
      const t = window.setTimeout(() => mobileInputRef.current?.focus(), 30);
      return () => window.clearTimeout(t);
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        rootRef.current?.contains(target) ||
        desktopPanelRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, [open, isMobile]);

  const showIdlePanel = open && draft.trim().length === 0;
  const showTypingPanel = open && draft.trim().length > 0;
  const hasSuggestHits = suggestions.length > 0;
  const showEmpty =
    showTypingPanel && suggestEnabled && suggestFetched && !hasSuggestHits;
  const desktopPanelOpen =
    !isMobile &&
    open &&
    (showIdlePanel || (showTypingPanel && (hasSuggestHits || showEmpty)));

  useLayoutEffect(() => {
    if (!desktopPanelOpen) return;
    const update = () => {
      const anchor = inputRef.current ?? rootRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const maxWidth = Math.min(560, window.innerWidth - 16);
      const width = Math.min(Math.max(rect.width, 280), maxWidth);
      const left = Math.min(
        Math.max(8, rect.left),
        Math.max(8, window.innerWidth - width - 8),
      );
      setDesktopPanelStyle({
        position: 'fixed',
        top: rect.bottom + 6,
        left,
        width,
        zIndex: 80,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [desktopPanelOpen, draft, recent.length, suggestions.length, showEmpty]);

  useEffect(() => {
    if (!open || !isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, isMobile]);

  useEffect(() => {
    const onSlash = (event: globalThis.KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      event.preventDefault();
      setOpen(true);
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', onSlash);
    return () => window.removeEventListener('keydown', onSlash);
  }, []);

  const selectableCount = showTypingPanel
    ? suggestions.length + companySuggestions.length + (hasSuggestHits ? 1 : 0)
    : recent.length;

  const nextHighlightKey = `${draft}|${suggestions.length}|${companySuggestions.length}|${showIdlePanel ? 'idle' : 'type'}`;
  if (nextHighlightKey !== highlightResetKey) {
    setHighlightResetKey(nextHighlightKey);
    setHighlightIndex(0);
  }

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setHighlightIndex((i) =>
        selectableCount === 0 ? 0 : Math.min(i + 1, selectableCount - 1),
      );
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (event.shiftKey) {
        commit(draft);
        return;
      }
      if (showTypingPanel && suggestions[highlightIndex]) {
        const lead = suggestions[highlightIndex]!;
        rememberRecent(draft);
        setOpen(false);
        onOpenLead(lead.id);
        return;
      }
      const companyIndex = highlightIndex - suggestions.length;
      if (
        showTypingPanel &&
        companyIndex >= 0 &&
        companyIndex < companySuggestions.length
      ) {
        commit(companySuggestions[companyIndex]!.name);
        return;
      }
      if (
        showTypingPanel &&
        hasSuggestHits &&
        highlightIndex === suggestions.length + companySuggestions.length
      ) {
        commit(draft);
        return;
      }
      if (showIdlePanel && recent[highlightIndex]) {
        commit(recent[highlightIndex]!);
        return;
      }
      commit(draft);
    }
  };

  const query = draft.trim();

  const leadSubtitle = (lead: Lead): ReactNode => {
    const company = lead.companyName?.trim();
    const email = lead.email?.trim();
    const status = resolvedStatusLabels[lead.status] ?? lead.status;
    const reasons = lead.matchReasons ?? [];
    const preferEmail =
      reasons.includes(LeadSearchMatchReason.EMAIL) &&
      !reasons.includes(LeadSearchMatchReason.NAME) &&
      email;
    const left = preferEmail
      ? highlightQuery(email, query)
      : company
        ? highlightQuery(company, query)
        : null;
    if (!left && !status) return null;
    return (
      <>
        {left}
        {left && status ? ' · ' : null}
        {status}
      </>
    );
  };

  const panelClass = cn(
    'overflow-hidden rounded-[14px] border border-border bg-card shadow-[0_18px_50px_rgba(0,0,0,0.16)]',
  );

  const showClear = draft.length > 0;

  const cancelSearch = () => {
    setDraft(value);
    setOpen(false);
  };

  const quickFilters = (
    <div className="flex flex-wrap gap-1.5">
      <button
        type="button"
        className="inline-flex h-[30px] shrink-0 items-center rounded-full border border-border bg-card px-[11px] text-[12.5px]"
        onClick={() => {
          onQuickFilterAssignedToMe();
          setOpen(false);
        }}
      >
        {labels.assignedToMe}
      </button>
      <button
        type="button"
        className="inline-flex h-[30px] shrink-0 items-center rounded-full border border-border bg-card px-[11px] text-[12.5px]"
        onClick={() => {
          onQuickFilterStatusNew();
          setOpen(false);
        }}
      >
        {labels.statusNew}
      </button>
      {eventQuickFilters.slice(0, 2).map((ev) => (
        <button
          key={ev.id}
          type="button"
          className="inline-flex h-[30px] shrink-0 items-center rounded-full border border-border bg-card px-[11px] text-[12.5px]"
          onClick={() => {
            onQuickFilterEvent?.(ev.id);
            setOpen(false);
          }}
        >
          {ev.name}
        </button>
      ))}
    </div>
  );

  const scopeChip = (
    chipScope: LeadListScope,
    label: string,
    count: number,
  ) => {
    const active = scope === chipScope;
    return (
      <button
        key={chipScope}
        type="button"
        className={cn(
          'inline-flex h-[30px] shrink-0 items-center gap-1.5 rounded-full border px-[11px] text-[12.5px] whitespace-nowrap',
          active
            ? 'border-foreground bg-foreground font-semibold text-background'
            : 'border-[color:var(--surface-border)] bg-card text-foreground',
        )}
        onClick={() => onScopeChange?.(chipScope)}
      >
        {label}
        <span
          className={cn(
            'font-mono text-[11px]',
            active ? 'opacity-65' : 'text-muted-foreground',
          )}
        >
          {count}
        </span>
      </button>
    );
  };

  const mobileScopeChips =
    showTypingPanel && onScopeChange && scopeCounts ? (
      <div className="flex gap-1.5 overflow-x-auto px-4 pt-3">
        {scopeChip(
          LeadListScope.WORKSPACE,
          labels.scopeWorkspace,
          scopeCounts.workspace,
        )}
        {scopeChip(LeadListScope.MINE, labels.scopeMine, scopeCounts.mine)}
        {canSeeOthersPrivate
          ? scopeChip(
              LeadListScope.OTHERS_PRIVATE,
              labels.scopeOthersPrivate,
              scopeCounts.othersPrivate,
            )
          : null}
      </div>
    ) : null;

  const renderLeadRow = (
    lead: Lead,
    index: number,
    variant: 'desktop' | 'mobile',
  ) => {
    const name =
      formatLeadDisplayName(lead.firstName, lead.lastName) || lead.email || '—';
    const isOthersPrivate =
      lead.visibility === 'private' && scope === LeadListScope.OTHERS_PRIVATE;
    const meta = reasonMeta(lead, query, labels);
    const selected = highlightIndex === index;

    if (variant === 'mobile') {
      return (
        <button
          key={lead.id}
          type="button"
          className="flex w-full items-center gap-3 border-b border-[color:var(--surface-border)] px-3.5 py-3 text-left last:border-b-0"
          onClick={() => {
            rememberRecent(draft);
            setOpen(false);
            onOpenLead(lead.id);
          }}
        >
          <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-[11px] bg-muted text-[14px] font-bold">
            {leadInitials(lead)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14px] font-semibold">
              {highlightQuery(name, query)}
            </span>
            <span className="mt-0.5 block truncate text-[12px] text-muted-foreground">
              {leadSubtitle(lead)}
            </span>
            <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              {isOthersPrivate ? (
                <>
                  <Lock className="size-[11px]" aria-hidden />
                  {labels.othersPrivateHint}
                </>
              ) : (
                <>
                  {meta.icon}
                  {meta.text}
                </>
              )}
            </span>
          </span>
          <ChevronRight
            className="size-4 shrink-0 text-muted-foreground"
            aria-hidden
          />
        </button>
      );
    }

    return (
      <button
        key={lead.id}
        type="button"
        role="option"
        aria-selected={selected}
        className={cn(
          'flex min-h-12 w-full items-center gap-2.5 px-3.5 py-1.5 text-left',
          selected && 'bg-[color:var(--ghost-accent-bg)]',
        )}
        onMouseEnter={() => setHighlightIndex(index)}
        onClick={() => {
          rememberRecent(draft);
          setOpen(false);
          onOpenLead(lead.id);
        }}
      >
        <span className="inline-flex size-[30px] shrink-0 items-center justify-center rounded-[9px] bg-muted text-[11px] font-bold">
          {leadInitials(lead)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold">
            {highlightQuery(name, query)}
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            {leadSubtitle(lead)}
          </span>
        </span>
        <span className="inline-flex max-w-[42%] shrink-0 items-center gap-1 truncate text-[11px] text-muted-foreground">
          {isOthersPrivate ? (
            <>
              <Lock className="size-[11px] shrink-0" aria-hidden />
              {labels.othersPrivateHint}
            </>
          ) : (
            <>
              {meta.icon}
              <span className="truncate">{meta.text}</span>
            </>
          )}
        </span>
      </button>
    );
  };

  const companyHint = (count: number) =>
    (count === 1
      ? labels.companyFilterHint
      : labels.companyFilterHintPlural
    ).replace('{count}', String(count));

  const mobileOverlay =
    isMobile && open && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="fixed inset-0 z-[80] flex flex-col bg-[color:var(--surface-1,#f4f5f8)]"
            role="dialog"
            aria-modal="true"
            aria-label={labels.placeholder}
          >
            <div className="flex items-center gap-2.5 px-4 pb-2 pt-[max(12px,env(safe-area-inset-top))]">
              <div className="relative min-w-0 flex-1">
                <Search
                  className="pointer-events-none absolute left-3.5 top-1/2 size-[17px] -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  ref={mobileInputRef}
                  type="search"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onInputKeyDown}
                  placeholder={labels.placeholder}
                  aria-label={labels.placeholder}
                  enterKeyHint="search"
                  className={cn(
                    'h-11 rounded-[12px] border-ring bg-card pl-10 text-[15px] shadow-[0_0_0_3px_color-mix(in_oklab,var(--ring)_18%,transparent)]',
                    HIDE_NATIVE_SEARCH_CLEAR,
                    showClear && 'pr-10',
                  )}
                />
                {showClear ? (
                  <button
                    type="button"
                    onClick={() => {
                      setDraft('');
                      onChange('');
                      onCommit('');
                      mobileInputRef.current?.focus();
                    }}
                    aria-label={labels.clearAriaLabel}
                    className="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground"
                  >
                    <X className="size-3.5" aria-hidden />
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                className="shrink-0 text-[14px] font-semibold text-[color:var(--ghost-accent-fg)]"
                onClick={cancelSearch}
              >
                {labels.cancelLabel}
              </button>
            </div>

            {mobileScopeChips}

            <div className="min-h-0 flex-1 overflow-y-auto pb-[max(16px,env(safe-area-inset-bottom))]">
              {showIdlePanel ? (
                <>
                  {recent.length > 0 ? (
                    <>
                      <div className="px-4 pb-1.5 pt-4 text-[10.5px] font-semibold tracking-[0.08em] text-muted-foreground">
                        {labels.recentHeading}
                      </div>
                      <div className="mx-4 overflow-hidden rounded-[var(--radius-card-mobile,16px)] border border-[color:var(--surface-border)] bg-card shadow-[var(--shadow-card)]">
                        {recent.map((item) => (
                          <div
                            key={item}
                            className="flex min-h-12 items-center gap-3 border-b border-[color:var(--surface-border)] px-3.5 last:border-b-0"
                          >
                            <button
                              type="button"
                              className="flex min-w-0 flex-1 items-center gap-3 text-left"
                              onClick={() => commit(item)}
                            >
                              <History
                                className="size-4 shrink-0 text-muted-foreground"
                                aria-hidden
                              />
                              <span className="truncate text-[14px]">
                                {item}
                              </span>
                            </button>
                            <button
                              type="button"
                              className="text-muted-foreground"
                              aria-label={labels.clearAriaLabel}
                              onClick={() => removeRecent(item)}
                            >
                              <X className="size-3.5" aria-hidden />
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : null}
                  <div className="px-4 pb-1.5 pt-5 text-[10.5px] font-semibold tracking-[0.08em] text-muted-foreground">
                    {labels.quickFiltersHeading}
                  </div>
                  <div className="px-4">{quickFilters}</div>
                </>
              ) : null}

              {showTypingPanel && hasSuggestHits ? (
                <div className="mx-4 mt-3 overflow-hidden rounded-[var(--radius-card-mobile,16px)] border border-[color:var(--surface-border)] bg-card shadow-[var(--shadow-card)]">
                  {suggestions.map((lead, index) =>
                    renderLeadRow(lead, index, 'mobile'),
                  )}
                </div>
              ) : null}

              {showTypingPanel && companySuggestions.length > 0 ? (
                <div className="mx-4 mt-2.5 overflow-hidden rounded-[var(--radius-card-mobile,16px)] border border-[color:var(--surface-border)] bg-card shadow-[var(--shadow-card)]">
                  {companySuggestions.map((company) => (
                    <button
                      key={company.name}
                      type="button"
                      className="flex w-full items-center gap-3 px-3.5 py-3 text-left"
                      onClick={() => commit(company.name)}
                    >
                      <Building2
                        className="size-4 shrink-0 text-muted-foreground"
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate text-[13.5px]">
                        {labels.companyPrefix}{' '}
                        {highlightQuery(company.name, query)}
                      </span>
                      <span className="text-[12px] text-muted-foreground">
                        {company.count}
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}

              {showEmpty ? (
                <div className="mx-4 mt-6 flex flex-col items-center gap-3 text-center">
                  <p className="text-[15px] font-bold">
                    {labels.emptyTitle.replace('{q}', draft.trim())}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {onResetFilters ? (
                      <button
                        type="button"
                        className="inline-flex h-10 items-center rounded-md border border-border bg-card px-4 text-[13px] font-semibold"
                        onClick={() => {
                          onResetFilters();
                          setOpen(false);
                        }}
                      >
                        {labels.resetFilters}
                      </button>
                    ) : null}
                    {onSwitchToWorkspaceScope &&
                    scope !== LeadListScope.WORKSPACE ? (
                      <button
                        type="button"
                        className="inline-flex h-10 items-center rounded-md bg-primary-solid px-4 text-[13px] font-semibold text-primary-solid-foreground hover:bg-primary-solid-hover"
                        onClick={() => {
                          onSwitchToWorkspaceScope();
                          setOpen(false);
                        }}
                      >
                        {labels.searchInWorkspace}
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>,
          document.body,
        )
      : null;

  const showAllIndex = suggestions.length + companySuggestions.length;

  return (
    <div ref={rootRef} className={cn('relative min-w-0', className)}>
      {hideIdleTrigger && !open ? null : (
        <div className="relative min-w-0">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-[15px] -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            ref={inputRef}
            type="search"
            value={isMobile && open ? value : draft}
            readOnly={isMobile}
            onChange={(e) => {
              if (isMobile) return;
              setDraft(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onClick={() => {
              if (isMobile) setOpen(true);
            }}
            onKeyDown={isMobile ? undefined : onInputKeyDown}
            placeholder={labels.placeholder}
            aria-label={labels.placeholder}
            aria-controls={listboxId}
            aria-expanded={open}
            aria-autocomplete="list"
            role="combobox"
            className={cn(
              inputClassName ?? listToolbarSearchInputClass,
              HIDE_NATIVE_SEARCH_CLEAR,
              showClear && !isMobile && 'pr-9',
              open &&
                !isMobile &&
                'border-ring shadow-[0_0_0_3px_color-mix(in_oklab,var(--ring)_18%,transparent)]',
            )}
          />
          {showClear && !isMobile ? (
            <button
              type="button"
              onClick={() => {
                setDraft('');
                onChange('');
                onCommit('');
                inputRef.current?.focus();
              }}
              aria-label={labels.clearAriaLabel}
              className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : !isMobile && showSlashHint ? (
            <span className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 items-center justify-center rounded-[5px] border border-border bg-muted px-1.5 font-mono text-[10.5px] text-muted-foreground sm:inline-flex">
              /
            </span>
          ) : null}
        </div>
      )}

      {mobileOverlay}

      {desktopPanelOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={desktopPanelRef}
              id={listboxId}
              className={panelClass}
              style={desktopPanelStyle}
              role={showEmpty ? undefined : 'listbox'}
            >
              {showIdlePanel ? (
                <>
                  {recent.length > 0 ? (
                    <>
                      <div className="px-3.5 pb-1 pt-2.5 text-[10.5px] font-semibold tracking-[0.08em] text-muted-foreground">
                        {labels.recentHeading}
                      </div>
                      {recent.map((item, index) => (
                        <div
                          key={item}
                          role="option"
                          aria-selected={highlightIndex === index}
                          className={cn(
                            'flex min-h-12 w-full items-center gap-2.5 px-3.5 py-1.5',
                            highlightIndex === index && 'bg-accent/40',
                          )}
                          onMouseEnter={() => setHighlightIndex(index)}
                        >
                          <button
                            type="button"
                            className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
                            onClick={() => commit(item)}
                          >
                            <span className="inline-flex size-[34px] shrink-0 items-center justify-center rounded-[10px] bg-muted text-muted-foreground">
                              <History className="size-[15px]" aria-hidden />
                            </span>
                            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">
                              {item}
                            </span>
                          </button>
                          <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground"
                            aria-label={labels.clearAriaLabel}
                            onClick={() => removeRecent(item)}
                          >
                            <X className="size-[13px]" aria-hidden />
                          </button>
                        </div>
                      ))}
                    </>
                  ) : null}
                  <div className="px-3.5 pb-1 pt-2.5 text-[10.5px] font-semibold tracking-[0.08em] text-muted-foreground">
                    {labels.quickFiltersHeading}
                  </div>
                  <div className="px-3.5 pb-3.5 pt-1">{quickFilters}</div>
                </>
              ) : null}

              {showTypingPanel && hasSuggestHits ? (
                <>
                  <div className="px-3.5 pb-1 pt-2.5 text-[10.5px] font-semibold tracking-[0.08em] text-muted-foreground">
                    {labels.leadsHeading}
                  </div>
                  {suggestions.map((lead, index) =>
                    renderLeadRow(lead, index, 'desktop'),
                  )}
                  {companySuggestions.length > 0 ? (
                    <>
                      <div className="px-3.5 pb-1 pt-2.5 text-[10.5px] font-semibold tracking-[0.08em] text-muted-foreground">
                        {labels.companiesHeading}
                      </div>
                      {companySuggestions.map((company, i) => {
                        const index = suggestions.length + i;
                        return (
                          <button
                            key={company.name}
                            type="button"
                            role="option"
                            aria-selected={highlightIndex === index}
                            className={cn(
                              'flex min-h-12 w-full items-center gap-2.5 px-3.5 py-1.5 text-left',
                              highlightIndex === index &&
                                'bg-[color:var(--ghost-accent-bg)]',
                            )}
                            onMouseEnter={() => setHighlightIndex(index)}
                            onClick={() => commit(company.name)}
                          >
                            <span className="inline-flex size-[34px] shrink-0 items-center justify-center rounded-[10px] bg-muted text-muted-foreground">
                              <Building2 className="size-[15px]" aria-hidden />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-[13px] font-semibold">
                                {highlightQuery(company.name, query)}
                              </span>
                              <span className="block truncate text-xs text-muted-foreground">
                                {companyHint(company.count)}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </>
                  ) : null}
                  <div className="flex items-center justify-between gap-2.5 border-t border-border bg-muted/40 px-3.5 py-2.5 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded-[5px] border border-border bg-muted px-1 font-mono text-[10.5px]">
                        ↑
                      </kbd>
                      <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded-[5px] border border-border bg-muted px-1 font-mono text-[10.5px]">
                        ↓
                      </kbd>
                      {labels.chooseHint}{' '}
                      <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded-[5px] border border-border bg-muted px-1 font-mono text-[10.5px]">
                        ↵
                      </kbd>{' '}
                      {labels.openHint}
                    </span>
                    <button
                      type="button"
                      className={cn(
                        'inline-flex items-center gap-1.5 font-semibold text-primary',
                        highlightIndex === showAllIndex && 'underline',
                      )}
                      onMouseEnter={() => setHighlightIndex(showAllIndex)}
                      onClick={() => commit(draft)}
                    >
                      {labels.showAllInList.replace(
                        '{count}',
                        String(suggestions.length),
                      )}
                      <List className="size-3.5" aria-hidden />
                    </button>
                  </div>
                </>
              ) : null}

              {showEmpty ? (
                <div className="flex flex-col items-center gap-2 px-[18px] py-[18px] text-center">
                  <div className="text-sm font-bold">
                    {labels.emptyTitle.replace('{q}', draft.trim())}
                  </div>
                  {fuzzySuggestions[0]?.query ? (
                    <button
                      type="button"
                      className="text-[12.5px] text-muted-foreground underline"
                      onClick={() =>
                        commit(
                          fuzzySuggestions[0]!.label?.trim() ||
                            fuzzySuggestions[0]!.query,
                        )
                      }
                    >
                      {labels.didYouMean.replace(
                        '{suggestion}',
                        fuzzySuggestions[0]!.label?.trim() ||
                          fuzzySuggestions[0]!.query,
                      )}
                    </button>
                  ) : null}
                  <div className="mt-1 flex flex-wrap justify-center gap-2">
                    {onResetFilters ? (
                      <button
                        type="button"
                        className="inline-flex h-9 items-center rounded-md border border-border bg-card px-3.5 text-[13px] font-semibold"
                        onClick={() => {
                          onResetFilters();
                          setOpen(false);
                        }}
                      >
                        {labels.resetFilters}
                      </button>
                    ) : null}
                    {onSwitchToWorkspaceScope &&
                    scope !== LeadListScope.WORKSPACE ? (
                      <button
                        type="button"
                        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-primary/30 bg-primary/10 px-3.5 text-[13px] font-semibold text-primary"
                        onClick={() => {
                          onSwitchToWorkspaceScope();
                          setOpen(false);
                        }}
                      >
                        {labels.searchInWorkspace}
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
